import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft, Megaphone, Plus, Play, X, Users, Target,
  Calendar, Clock, RefreshCw, Trash2, TrendingUp,
} from 'lucide-react';
import useApiMethods from '../../../lib/useApiMethods';
import { cn } from '../../../utils/tailwindUtils';
import ToastMsg from '../../../components/common/ToastMsg';
import {
  ENGAGE_AI_CALLER,
} from './aiCallerConstants';
import { ENGAGE_SOURCE_OPTIONS } from '../../../utils/engageSourceLinks';
import { ENGAGE_STATUS_LABEL } from '../../../utils/engageStageBadge';


const CAMPAIGN_STATUS_BADGE = {
  draft:     'bg-slate-100 text-slate-700 dark:bg-slate-900/30 dark:text-slate-300',
  scheduled: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  running:   'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  paused:    'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  completed: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};


export default function EngageAICallerCampaigns() {
  const navigate = useNavigate();
  const { fetch: apiFetch, create: apiCreate, remove: apiDelete } = useApiMethods({ throwOnFail: true });

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [reloadTick, setReloadTick] = useState(0);

  // Wizard state
  const [name, setName] = useState('');
  const [scheduleAt, setScheduleAt] = useState('');
  const [sources, setSources] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await apiFetch(ENGAGE_AI_CALLER.CAMPAIGNS);
      setCampaigns(resp?.data || resp?.data?.data || []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTick]);

  useEffect(() => { load(); }, [load]);

  const runPreview = async () => {
    setPreviewLoading(true);
    try {
      const audience_filter = {
        target: 'engage',
        ...(sources.length   ? { enquiry_source: sources } : {}),
        ...(statuses.length ? { enquiry_status: statuses } : {}),
      };
      const resp = await apiCreate(ENGAGE_AI_CALLER.CAMPAIGNS_PREVIEW, { audience_filter });
      setPreview(resp?.data || resp?.data?.data || null);
    } catch (e) {
      ToastMsg('error', 'Preview failed');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!name.trim()) return ToastMsg('error', 'Campaign name is required');
    const audience_filter = {
      target: 'engage',
      ...(sources.length   ? { enquiry_source: sources } : {}),
      ...(statuses.length ? { enquiry_status: statuses } : {}),
    };
    try {
      await apiCreate(ENGAGE_AI_CALLER.CAMPAIGNS, {
        name: name.trim(),
        audience_filter,
        schedule_at: scheduleAt || null,
        status: scheduleAt ? 'scheduled' : 'draft',
      });
      ToastMsg('success', 'Campaign created');
      setWizardOpen(false);
      setName(''); setScheduleAt(''); setSources([]); setStatuses([]); setPreview(null);
      setReloadTick(t => t + 1);
    } catch (e) {
      ToastMsg('error', 'Failed to create');
    }
  };

  const handleStart = async (id) => {
    try {
      await apiCreate(`${ENGAGE_AI_CALLER.CAMPAIGNS}${id}/start/`, {});
      ToastMsg('success', 'Campaign started');
      setReloadTick(t => t + 1);
    } catch (e) {
      ToastMsg('error', e?.response?.data?.message || 'Failed to start');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    try {
      await apiDelete(`${ENGAGE_AI_CALLER.CAMPAIGNS}${id}/`);
      ToastMsg('success', 'Deleted');
      setReloadTick(t => t + 1);
    } catch {
      ToastMsg('error', 'Failed to delete');
    }
  };

  const toggleInList = (list, setList, value) =>
    list.includes(value) ? setList(list.filter(x => x !== value)) : setList([...list, value]);

  return (
    <div className="p-4 max-w-[1400px] mx-auto space-y-4">
      <button onClick={() => navigate('/engage/ai-caller/dashboard')} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back to AI Voice Agent
      </button>

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-pink-600 flex items-center justify-center text-white shadow-sm">
            <Megaphone className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Campaigns</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Batch AI calls to targeted enquiry cohorts</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setReloadTick(t => t + 1)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button onClick={() => setWizardOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-white bg-gradient-to-r from-orange-500 to-pink-600">
            <Plus className="w-3.5 h-3.5" /> New campaign
          </button>
        </div>
      </div>

      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3">
        {loading ? <p className="col-span-3 text-center text-gray-500 py-8 dark:text-gray-400">Loading…</p>
        : campaigns.length === 0 ? <p className="col-span-3 text-center text-gray-500 py-8 dark:text-gray-400">No campaigns yet. Click "New campaign" to start.</p>
        : campaigns.map(c => {
          const af = c.audience_filter || {};
          const convPct = c.total_leads ? Math.round((c.demo_booked_count || 0) / c.total_leads * 100) : 0;
          return (
            <div key={c.id} className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700">
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="font-semibold text-gray-900 dark:text-white flex-1 min-w-0 truncate">{c.name}</h3>
                <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium capitalize', CAMPAIGN_STATUS_BADGE[c.status] || 'bg-gray-100 text-gray-700')}>
                  {c.status}
                </span>
              </div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-3 space-y-1">
                {af.enquiry_source?.length > 0 && <div><Target className="w-3 h-3 inline mr-1" />Sources: {af.enquiry_source.join(', ')}</div>}
                {af.enquiry_status?.length > 0 && <div><Users className="w-3 h-3 inline mr-1" />Stages: {af.enquiry_status.map(s => ENGAGE_STATUS_LABEL[s] || s).join(', ')}</div>}
                {c.schedule_at && <div><Calendar className="w-3 h-3 inline mr-1" />Scheduled: {new Date(c.schedule_at).toLocaleString()}</div>}
                {c.started_at  && <div><Clock className="w-3 h-3 inline mr-1" />Started: {new Date(c.started_at).toLocaleString()}</div>}
              </div>

              <div className="grid grid-cols-4 gap-2 mb-3">
                <div className="text-center">
                  <div className="text-[10px] uppercase text-gray-500">Total</div>
                  <div className="text-sm font-bold text-gray-900 dark:text-white">{c.total_leads}</div>
                </div>
                <div className="text-center">
                  <div className="text-[10px] uppercase text-gray-500">Called</div>
                  <div className="text-sm font-bold text-blue-600 dark:text-blue-400">{c.called_count}</div>
                </div>
                <div className="text-center">
                  <div className="text-[10px] uppercase text-gray-500">Demos</div>
                  <div className="text-sm font-bold text-green-600 dark:text-green-400">{c.demo_booked_count}</div>
                </div>
                <div className="text-center">
                  <div className="text-[10px] uppercase text-gray-500">Failed</div>
                  <div className="text-sm font-bold text-red-500">{c.failed_count}</div>
                </div>
              </div>

              {c.total_leads > 0 && (
                <div className="mb-3">
                  <div className="flex items-center justify-between text-[10px] text-gray-500 mb-0.5">
                    <span>Conversion</span><span className="font-bold">{convPct}%</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full" style={{ width: `${convPct}%` }} />
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                {(c.status === 'draft' || c.status === 'paused') && (
                  <button onClick={() => handleStart(c.id)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs text-white bg-gradient-to-r from-green-500 to-emerald-600">
                    <Play className="w-3 h-3" /> Start
                  </button>
                )}
                <button onClick={() => handleDelete(c.id)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 ml-auto">
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Wizard modal */}
      {wizardOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setWizardOpen(false)}>
          <div onClick={e => e.stopPropagation()} className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 dark:text-white">New AI campaign</h2>
              <button onClick={() => setWizardOpen(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 flex-1 overflow-y-auto space-y-4">
              <div>
                <label className="text-[11px] uppercase text-gray-500 font-medium">Campaign name *</label>
                <input value={name} onChange={e => setName(e.target.value)}
                  placeholder="e.g. Diwali no-show recovery"
                  className="mt-1 w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white" />
              </div>
              <div>
                <label className="text-[11px] uppercase text-gray-500 font-medium">Schedule at (optional)</label>
                <input type="datetime-local" value={scheduleAt} onChange={e => setScheduleAt(e.target.value)}
                  className="mt-1 w-full px-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white" />
                <p className="text-[10px] text-gray-500 mt-1">Leave empty to save as draft and start manually.</p>
              </div>

              <div>
                <label className="text-[11px] uppercase text-gray-500 font-medium">Enquiry sources (optional)</label>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {ENGAGE_SOURCE_OPTIONS.map(o => (
                    <button key={o.value} onClick={() => toggleInList(sources, setSources, o.value)}
                      className={cn('px-2.5 py-1 rounded-full text-xs',
                        sources.includes(o.value)
                          ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300')}>
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[11px] uppercase text-gray-500 font-medium">Enquiry stages (optional)</label>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {Object.entries(ENGAGE_STATUS_LABEL).map(([v, l]) => (
                    <button key={v} onClick={() => toggleInList(statuses, setStatuses, v)}
                      className={cn('px-2.5 py-1 rounded-full text-xs',
                        statuses.includes(v)
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300')}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={runPreview} disabled={previewLoading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20">
                <TrendingUp className="w-3.5 h-3.5" /> {previewLoading ? 'Previewing…' : 'Preview matching enquiries'}
              </button>

              {preview && (
                <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-3 text-sm">
                  <div className="font-semibold text-blue-700 dark:text-blue-300">
                    {preview.total} enquiries match this audience
                  </div>
                  {preview.sample?.length > 0 && (
                    <div className="mt-2 text-xs text-blue-700 dark:text-blue-300 space-y-0.5">
                      Sample: {preview.sample.slice(0, 5).map(s => s.name).join(', ')}
                      {preview.sample.length > 5 && ` … +${preview.total - 5} more`}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-2">
              <button onClick={() => setWizardOpen(false)} className="px-3 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleCreate} className="px-3 py-1.5 rounded-lg text-sm text-white bg-gradient-to-r from-orange-500 to-pink-600">
                Create campaign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
